<template>
  <Collection :collection-id="collectionId" class="collection-container">
    <template
      #collection-details="{
        datasetList,
        dimensionsInfo,
        dimensionsNames,
        hasDatasets,
        datasetsCount,
      }"
    >
      <table
        v-if="hasDatasets"
        :key="`staged-${collectionId}`"
        class="dataloader"
      >
        <thead>
          <tr>
            <th class="index"></th>
            <th class="dataset-name">
              <span>Dataset Name</span>
            </th>
            <th class="name">
              <span>Datafiles</span>
            </th>
            <th class="frozen-right frozen-header">
              <!-- Show expand controls when there are expandable folders -->
              <fieldset
                v-if="hasExpandableFolders"
                v-tooltip="{
                  content: expandDatasetHTMLTooltip,
                  noAutoFocus: true,
                  html: true,
                }"
                class="field has-addons"
                :disabled="isExpandingSome"
              >
                <div class="control">
                  <input
                    v-model="expandPattern"
                    class="input is-small"
                    type="text"
                    placeholder="e.g. *.czi"
                    :style="{ 'field-sizing': 'content', 'min-width': '70px' }"
                    @keyup.enter="handleExpandAll"
                  />
                </div>
                <div class="control">
                  <BaseButton
                    class="custom-button"
                    size="small"
                    variant="primary"
                    :outlined="true"
                    label="Expand All"
                    :icon="['fa-solid', 'fa-box-open']"
                    :loading="isExpandingSome"
                    @keyup.enter="handleExpandAll"
                    @click="handleExpandAll"
                  />
                </div>
              </fieldset>
              <!-- Show cancel all button when there are expanded datasets (only if no expandable folders left) -->
              <div v-else-if="hasExpandedDatasets">
                <BaseButton
                  v-tooltip="
                    'Cancel all expansions and restore original folders'
                  "
                  class="custom-button"
                  size="small"
                  variant="danger"
                  :outlined="true"
                  label="Cancel All"
                  :icon="['fa-solid', 'fa-times']"
                  @click="handleCollapseAll"
                />
              </div>
            </th>
            <th class="frozen-right frozen-header">
              <div v-if="hasDatasets">
                <BaseButton
                  v-tooltip="`Remove ${datasetsCount} datasets from collection`"
                  size="small"
                  label="Unstage All"
                  @click="handleUnstageAll(collectionId)"
                />
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(dataset, datasetIndex) of datasetList"
            :key="`dataset-${datasetIndex}`"
            class="dataset-row"
            style="padding: 5px"
          >
            <td class="index-cell">
              <span>
                {{ datasetIndex + 1 }}
              </span>
            </td>
            <td class="dataset-name">
              <div>
                <input
                  :value="dataset.name"
                  class="input dataset-name is-small"
                  @change="
                    renameDataset({
                      id: dataset.id,
                      name: $event.target.value,
                    })
                  "
                />
              </div>
            </td>
            <td>
              <p v-if="dataset.files.length === 0" class="help is-danger m-0">
                <em>No file</em>
                <em v-if="dataset.expansion && dataset.expansion.expandPattern">
                  matching pattern "{{ dataset.expansion.expandPattern }}"
                </em>
              </p>
              <div v-else>
                <p
                  v-if="dataset.files.length > 1"
                  class="help has-text-grey mb-2"
                  style="margin-top: 0"
                >
                  ({{ dataset.files.length }} datafiles)
                </p>
                <p
                  v-for="(datafile, dfIndex) of dataset.files"
                  :key="datafile.id"
                >
                  <span
                    v-if="
                      dimensionsInfo[dfIndex] &&
                      dimensionsInfo[dfIndex].id &&
                      dimensionsNames[dfIndex]
                    "
                    class="tag is-info has-text-weight-bold mx-2"
                  >
                    {{ dimensionsNames[dfIndex] }}
                  </span>
                  <span v-if="datafile.is_dir">
                    <span class="icon mr-2">
                      <i class="fa fa-regular fa-folder" aria-hidden="true"></i>
                    </span>
                  </span>
                  <span v-else>
                    <span class="icon mr-2">
                      <i class="fa solid fa-file" aria-hidden="true"></i>
                    </span>
                  </span>
                  <span>
                    {{ datafile.name }}
                    <span v-if="datafile.is_dir">/</span>
                  </span>
                </p>
              </div>
            </td>
            <td
              class="frozen-right frozen-cell"
              style="
                vertical-align: top;
                border-left: none;
                min-width: 1%;
                white-space: nowrap;
              "
            >
              <div
                class="is-flex is-flex-direction-column is-align-items-flex-end"
              >
                <!-- Show expand button with input when not expanded -->
                <fieldset
                  v-if="
                    dataset.expansion &&
                    dataset.expansion.expandable &&
                    !dataset.expansion.isExpanded
                  "
                  v-tooltip="{
                    content: expandDatasetHTMLTooltip,
                    noAutoFocus: true,
                    html: true,
                  }"
                  class="field has-addons"
                  :disabled="!!dataset.expansion.isExpanding"
                >
                  <div class="control" style="min-width: 80px">
                    <input
                      :value="dataset.expansion.expandPattern"
                      class="input is-small"
                      type="text"
                      placeholder="e.g. *.czi"
                      :style="{ 'field-sizing': 'content' }"
                      @input="
                        updateExpandPattern(dataset.id, $event.target.value)
                      "
                      @keyup.enter="handleExpand(dataset.id)"
                    />
                  </div>
                  <div class="control">
                    <BaseButton
                      class="custom-button"
                      size="small"
                      variant="primary"
                      :outlined="true"
                      label="Expand"
                      :icon="['fa-solid', 'fa-box-open']"
                      :loading="!!dataset.expansion.isExpanding"
                      @keyup.enter="handleExpand(dataset.id)"
                      @click="handleExpand(dataset.id)"
                    />
                  </div>
                </fieldset>
                <!-- Show cancel button when expanded -->
                <BaseButton
                  v-if="dataset.expansion && dataset.expansion.isExpanded"
                  v-tooltip="'Use the parent folder as the datafile'"
                  class="custom-button"
                  size="small"
                  variant="danger"
                  :outlined="true"
                  label="Cancel expansion"
                  :icon="['fa-solid', 'fa-times']"
                  @click="collapseDataset(dataset.id)"
                />
              </div>
            </td>
            <td
              class="frozen-right frozen-cell"
              style="width: 1%; white-space: nowrap"
            >
              <div>
                <BaseButton
                  v-tooltip="`Remove dataset from collection`"
                  class="custom-button"
                  size="small"
                  label="Unstage"
                  @click="removeDataset(dataset.id)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </Collection>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import Collection from '@/components/data-loader/Collection.vue'

const expandDatasetHTMLTooltip = `
<div>
  <b
    class="has-text-primary has-text-weight-bold is-underlined"
  >
    Expand a dataset folder
  </b>:
  <p class="mt-2">
    Recursively extract individual files within a dataset folder to consider
    each file as an independant datafile within this dataset.
  </p>
  <p class="mb-2">
    <em>Optionally, provide an extension pattern to filter extracted files.</em>
  </p>
  <p>
    When not expanded, the parent folder is registered both as the dataset
    and as the only datafile of this dataset.
  </p>
</div>
`

export default {
  name: 'StagedDatasetViewer',
  components: {
    Collection,
  },
  props: {
    collectionId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      expandDatasetHTMLTooltip,
      expandPattern: '',
    }
  },
  computed: {
    ...mapState('dataloader/datasets', ['datasets']),
    ...mapGetters('dataloader/datasets', [
      'hasExpandableFolders',
      'hasExpandedDatasets',
      'isExpandingSome',
    ]),
  },
  methods: {
    ...mapActions('dataloader/datasets', [
      'renameDataset',
      'removeDataset',
      'expandDataset',
      'collapseDataset',
      'expandAllDatasets',
      'collapseAllDatasets',
      'removeCollectionDatasets',
    ]),
    async handleExpand(datasetId) {
      const pattern = this.datasets[datasetId].expansion.expandPattern
      await this.expandDataset({ datasetId, pattern })
    },
    async handleExpandAll() {
      await this.expandAllDatasets(this.expandPattern)
    },
    async handleCollapseAll() {
      await this.collapseAllDatasets()

      // After collapsing, re-propagate the global pattern to all expandable datasets
      this.$nextTick(() => {
        if (this.expandPattern) {
          Object.keys(this.datasets).forEach((id) => {
            const dataset = this.datasets[id]
            if (dataset.expansion && dataset.expansion.expandable) {
              this.updateExpandPattern(id, this.expandPattern)
            }
          })
        }
      })
    },
    async handleUnstageAll(collectionId) {
      await this.removeCollectionDatasets(collectionId)
    },
    updateExpandPattern(datasetId, pattern) {
      // Update pattern in store
      this.$store.commit('dataloader/datasets/SET_EXPANSION_STATE', {
        datasetId,
        expansionData: { expandPattern: pattern },
      })
    },
  },
  watch: {
    expandPattern(newPattern) {
      // Update all expandable datasets with the global pattern
      Object.keys(this.datasets).forEach((id) => {
        const dataset = this.datasets[id]
        if (dataset.expansion && dataset.expansion.expandable) {
          this.updateExpandPattern(id, newPattern)
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
td.dataset-name {
  vertical-align: top;
}
</style>
